import React, { useEffect, useState } from "react";
import { BASE_URL } from "../config/api";
import { IMG_URL } from "../config/api";
import { useNavigate } from "react-router-dom";
import jsPDF from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";

const PlatingList = () => {
  const [platingRecords, setPlatingRecords] = useState([]);
  const [filteredPlatingRecords, setFilteredPlatingRecords] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        // Fetch staff list
        const staffResponse = await fetch(`${BASE_URL}staff`);
        const staffData = await staffResponse.json();

        let staffListData = [];
        if (staffData.status === 1 && staffData.data) {
          staffListData = staffData.data;
          setStaffList(staffListData);
        }

        // Fetch plating records
        const platingResponse = await fetch(`${BASE_URL}plating`);
        const platingData = await platingResponse.json();

        if (platingData.status === 1 && platingData.data) {
          // Map staff names to plating records
          const recordsWithStaffNames = platingData.data.map((record) => {
            const staff = staffListData.find((s) => s.id == record.staff_id);
            return {
              ...record,
              staff_name: staff ? staff.name : `Staff ID: ${record.staff_id}`,
            };
          });

          setPlatingRecords(recordsWithStaffNames);
          setFilteredPlatingRecords(recordsWithStaffNames);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  // Get staff name by ID
  const getStaffName = (staffId) => {
    if (!staffId || !staffList.length) return "N/A";
    const staff = staffList.find((s) => s.id == staffId);
    return staff ? staff.name : `Staff ID: ${staffId}`;
  };

  const handleSearchChange = (event) => {
    const term = event.target.value;
    setSearchTerm(term);
    filterPlatingRecords(term, startDate, endDate);
  };

  const handleDateChange = () => {
    filterPlatingRecords(searchTerm, startDate, endDate);
  };

  const filterPlatingRecords = (term, start, end) => {
    const termLower = term.toLowerCase();
    let filtered = platingRecords.filter((record) => {
      // Search in multiple fields including staff name
      const matchesSearch =
        (record.customer_name &&
          record.customer_name.toLowerCase().includes(termLower)) ||
        (record.staff_name &&
          record.staff_name.toLowerCase().includes(termLower));

      const recordDate = new Date(record.created_at)
        .toISOString()
        .split("T")[0];
      const matchesDateRange =
        (!start || recordDate >= start) && (!end || recordDate <= end);
      return matchesSearch && matchesDateRange;
    });
    setFilteredPlatingRecords(filtered);
  };

  const handleExportPDF = () => {
    const doc = new jsPDF();
    doc.text("Plating Records", 14, 10);

    const tableData = filteredPlatingRecords.map((record) => [
      record.staff_name || getStaffName(record.staff_id) || "N/A",
      record.customer_name || "N/A",
      record.net_weight_of_gold || "N/A",
      record.description || "N/A",
      record.pcs || "N/A",
      record.laker || "N/A",
      new Date(record.created_at).toLocaleDateString(),
    ]);

    doc.autoTable({
      head: [
        [
          "Staff Name",
          "Customer Name",
          "Net Gold",
          "Rhodium",
          "PCS",
          "Laker",
          "Date",
        ],
      ],
      body: tableData,
    });

    doc.save("plating-records.pdf");
  };

  const handleExportExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(
      filteredPlatingRecords.map((record) => ({
        StaffName: record.staff_name || getStaffName(record.staff_id) || "N/A",
        CustomerName: record.customer_name || "N/A",
        NetWeightOfGold: record.net_weight_of_gold || "N/A",
        Rhodium: record.description || "N/A",
        PCS: record.pcs || "N/A",
        Laker: record.laker || "N/A",
        Date: new Date(record.created_at).toLocaleDateString(),
      }))
    );

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Plating Records");
    XLSX.writeFile(workbook, "plating-records.xlsx");
  };

  const calculateTotals = () => {
    return filteredPlatingRecords.reduce(
      (totals, record) => {
        totals.netWeightOfGold += parseFloat(record.net_weight_of_gold) || 0;
        totals.pcs += parseInt(record.pcs, 10) || 0;
        totals.rodium += parseFloat(record.description) || 0;
        totals.laker += parseFloat(record.laker) || 0;
        return totals;
      },
      { netWeightOfGold: 0, pcs: 0, rodium: 0, laker: 0 }
    );
  };

  // Parse image safely
  const parseImage = (imageString) => {
    try {
      if (!imageString) return [];
      const parsed = JSON.parse(imageString);
      return Array.isArray(parsed) ? parsed : [parsed];
    } catch (error) {
      return [];
    }
  };

  const totals = calculateTotals();

  if (isLoading) {
    return (
      <div id="main">
        <div className="page-heading">
          <h3>Plating Records</h3>
        </div>
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: "50vh" }}
        >
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <span className="ms-3">Loading plating records...</span>
        </div>
      </div>
    );
  }

  return (
    <div id="main">
      <header className="mb-3">
        <a href="#" className="burger-btn d-block d-xl-none">
          <i className="bi bi-justify fs-3"></i>
        </a>
      </header>

      <div className="page-heading">
        <div className="page-title">
          <div className="row">
            <div className="col-12 col-md-6 order-md-1 order-last">
              <h3>Plating Records</h3>
            </div>
          </div>
        </div>
      </div>

      <section className="section">
        <div className="row">
          <div className="col-12 col-md-12">
            <div className="card">
              <div className="card-header">
                <div className="row">
                  <div className="col-md-6"></div>
                  <div className="col-md-6 text-end">
                    <button
                      className="btn btn-secondary ms-2"
                      onClick={handleExportPDF}
                    >
                      Export to PDF
                    </button>
                    <button
                      className="btn btn-success ms-2"
                      onClick={handleExportExcel}
                    >
                      Export to Excel
                    </button>
                  </div>
                </div>
              </div>
              <div className="card-body">
                <div className="row mb-3">
                  <div className="col-md-4">
                    <label className="form-label">Search</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search by Staff or Customer Name"
                      value={searchTerm}
                      onChange={handleSearchChange}
                    />
                  </div>
                  <div className="col-md-4">
                    <label htmlFor="startDate" className="form-label">
                      Start Date
                    </label>
                    <input
                      type="date"
                      id="startDate"
                      className="form-control"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                  </div>
                  <div className="col-md-4">
                    <label htmlFor="endDate" className="form-label">
                      End Date
                    </label>
                    <input
                      type="date"
                      id="endDate"
                      className="form-control"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </div>
                  <div className="col-md-4 mt-2">
                    <button
                      className="btn btn-secondary"
                      onClick={handleDateChange}
                    >
                      Apply Date Filter
                    </button>
                  </div>
                </div>

                <div className="table-responsive">
                  <table className="table table-lg">
                    <thead>
                      <tr>
                        <th>Staff Name</th>
                        <th>Customer Name</th>
                        <th>Net Weight of Gold</th>
                        <th>Rhodium</th>
                        <th>Flash</th>
                        <th>Laker</th>
                        <th>Image</th>
                        <th className="text-end">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredPlatingRecords.length > 0 ? (
                        filteredPlatingRecords.map((item) => {
                          const images = parseImage(item.image);
                          return (
                            <tr key={item.id}>
                              <td>
                                {item.staff_name ||
                                  getStaffName(item.staff_id) ||
                                  "N/A"}
                              </td>
                              <td>{item.customer_name}</td>
                              <td>{item.net_weight_of_gold}</td>
                              <td>{item.description}</td>
                              <td>{item.pcs}</td>
                              <td>{item.laker}</td>
                              <td>
                                {images.length > 0 ? (
                                  images.map((imagePath, index) => (
                                    <img
                                      key={index}
                                      src={`${IMG_URL}${imagePath.replace(
                                        /\\/g,
                                        "/"
                                      )}`}
                                      alt={`Plating ${index + 1}`}
                                      style={{
                                        width: "50px",
                                        height: "50px",
                                        objectFit: "cover",
                                        marginRight: "5px",
                                      }}
                                      onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src =
                                          "https://via.placeholder.com/50x50?text=No+Image";
                                      }}
                                    />
                                  ))
                                ) : (
                                  <span>No Image</span>
                                )}
                              </td>
                              <td className="text-end">
                                <button
                                  className="btn btn-danger ms-2"
                                  onClick={() => {
                                    if (
                                      window.confirm(
                                        "Are you sure you want to delete this record?"
                                      )
                                    ) {
                                      fetch(`${BASE_URL}plating/${item.id}`, {
                                        method: "DELETE",
                                        headers: {
                                          "Content-Type": "application/json",
                                        },
                                      })
                                        .then((response) => response.json())
                                        .then((data) => {
                                          if (data.status === 1) {
                                            setPlatingRecords(
                                              platingRecords.filter(
                                                (record) =>
                                                  record.id !== item.id
                                              )
                                            );
                                            setFilteredPlatingRecords(
                                              filteredPlatingRecords.filter(
                                                (record) =>
                                                  record.id !== item.id
                                              )
                                            );
                                          }
                                        })
                                        .catch((error) =>
                                          console.error(
                                            "Error deleting record:",
                                            error
                                          )
                                        );
                                    }
                                  }}
                                >
                                  Delete
                                </button>
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan="8" className="text-center">
                            No records found
                          </td>
                        </tr>
                      )}
                      <tr>
                        <td>
                          <strong>Total</strong>
                        </td>
                        <td></td>
                        <td>
                          <strong>{totals.netWeightOfGold.toFixed(2)}</strong>
                        </td>
                        <td>
                          <strong>{totals.rodium.toFixed(2)}</strong>
                        </td>
                        <td>
                          <strong>{totals.pcs}</strong>
                        </td>
                        <td>
                          <strong>{totals.laker.toFixed(2)}</strong>
                        </td>
                        <td colSpan="2"></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PlatingList;
